<!--

	Responsive panel containing the cover image and the video play button that opens hidden by default video modal.

-->
<template>
	<div class="mia-CoverVideoPanel">
		<div class="container">
			<div v-tr="'LandingPage_CoverPanel_Motto'" class="mia-cover-text" />
			<div v-if="videoUrl" class="mia-play-button-wrapper" :class="[excludedFromClickOutsideClass]">
				<div class="mia-play-button" @click.stop="open">
					<inline-svg src="@theme/icons/play_video.svg" />
				</div>
			</div>
			<img src="@theme/parents/landingpage/coverImage.png" class="mia-cover-image" :alt="$tr('CoverImageAlt')">
			<lightbox v-if="videoUrl" v-show="isVideoOpen" @close="close">
				<vimeo-player ref="vimeoPlayer" :video-src="videoUrl" :enable-google-tracking="true" />
			</lightbox>
		</div>
	</div>
</template>
<script lang="ts">
import { Component, Vue, Prop, toNative } from "vue-facing-decorator";
import Lightbox from "@/Control/Vue/Lesson/Lightbox.vue";
import VimeoPlayer, { type VimeoPlayer as VimeoPlayerType } from "@/Control/Vue/VimeoPlayer.vue";
import InlineSvg from "vue-inline-svg";
import ClickOutsideHelper from "@/utils/ClickOutsideHelper";

@Component({
	components: {
		Lightbox,
		VimeoPlayer,
		InlineSvg,
	}
})
export class CoverVideoPanel extends Vue {
	@Prop({ required: false, type: String, default: null }) videoUrl!: string | null;

	vimeoPlayer!: VimeoPlayerType;
	isVideoOpen = false;
	excludedFromClickOutsideClass = ClickOutsideHelper.EXCLUDED_FROM_CLICK_OUTSIDE_CLASS;

	mounted() : void {
		this.vimeoPlayer = this.$refs.vimeoPlayer as VimeoPlayerType;
	}

	close() : void {
		this.isVideoOpen = false;
		void this.vimeoPlayer.pause();
	}

	open() : void {
		this.isVideoOpen = true;
		void this.vimeoPlayer.setVideoProgress(0);
		void this.vimeoPlayer.play();
	}
}

export default toNative(CoverVideoPanel);
</script>
<style lang="scss">
.mia-CoverVideoPanel {
	// Local variables which are intended to be used in this component only

	// The height of the whole section
	$mia-cover-video-panel-height-desktop: 550px;
	$mia-cover-video-panel-height-tablet: 350px;
	$mia-cover-video-panel-height-mobile: 385px;

	// The ellipse white gradient which is used to highlight the image from behind
	$mia-cover-video-panel-white-gradient-desktop: radial-gradient(closest-side at 60% 50%, rgba(white, 0.5), transparent);
	$mia-cover-video-panel-white-gradient-mobile: radial-gradient(closest-side at 50% 60%, rgba(white, 0.5), transparent);

	// As cover image is anchored to the right side of the container
	// wee need to offset it in order not to overlap with the text
	$mia-cover-image-right-offset-desktop: -210px;
	$mia-cover-image-right-offset-tablet: -106px;
	$mia-cover-image-right-offset-mobile: calc(50vw - 255px);

	// We position the play button according to image position in the way it doesn't overlap faces
	$mia-play-button-right-offset-desktop: 278px;
	$mia-play-button-right-offset-tablet: 204px;
	$mia-play-button-top-offset-mobile: 102px;

	// We limit the width of the text block so it doesn't overlap the image
	$mia-cover-text-max-width-desktop: 600px;
	$mia-cover-text-max-width-tablet-big: 400px;
	$mia-cover-text-max-width-tablet-small: 325px;

	// We don't have such sizes so we add them here to be consistent with the design
	$mia-cover-text-font-size-desktop: 64px;
	$mia-cover-text-line-height-mobile: 32px;

	height: $mia-cover-video-panel-height-desktop;
	overflow: hidden;
	background: $mia-cover-video-panel-white-gradient-desktop, theme.$mia-color-themeMain;

	.container {
		height: 100%;
		position: relative;
		display: flex;
		align-items: center;
		padding: 0;
	}

	.mia-cover-image {
		height: 100%;
		right: $mia-cover-image-right-offset-desktop;
		bottom: 0;
		position: absolute;
	}

	.mia-cover-text {
		font-size: $mia-cover-text-font-size-desktop;
		z-index: theme.$mia-zIndex-1--foreground;
		color: theme.$mia-color-white;
		max-width: $mia-cover-text-max-width-desktop;
	}

	.mia-play-button-wrapper {
		flex-grow: 1;
		z-index: theme.$mia-zIndex-1--foreground;
	}

	.mia-play-button {
		float: right;
		margin-right: $mia-play-button-right-offset-desktop;
		width: theme.$mia-icon-size-huge;
		height: theme.$mia-icon-size-huge;
		border-radius: theme.$mia-border-radius-circle;
		background-color: theme.$mia-color-landingPage-playIcon;
		display: flex;
		justify-content: center;
		align-items: center;
		transition: transform 0.2s;
		cursor: pointer;

		// Positioning the white triangle right in the center of the button
		svg {
			width: 32%;
			height: 36%;
			transform: translateX(15%);
		}

		&:hover {
			transform: scale(1.15);
		}
	}

	@media (max-width: theme.$screen-md-max) {
		.mia-cover-text {
			font-size: theme.$font-size-huge;
			max-width: $mia-cover-text-max-width-tablet-big;
		}
	}

	@media (max-width: theme.$screen-sm-max) {
		height: $mia-cover-video-panel-height-tablet;

		.mia-cover-image {
			right: $mia-cover-image-right-offset-tablet;
		}

		.mia-cover-text {
			font-size: theme.$font-size-h1;
			max-width: $mia-cover-text-max-width-tablet-small;
		}

		.mia-play-button {
			width: theme.$mia-icon-size * 2;
			height: theme.$mia-icon-size * 2;
			margin-right: $mia-play-button-right-offset-tablet;
		}
	}

	@media (max-width: theme.$screen-xs-max) {
		height: $mia-cover-video-panel-height-mobile;
		background: $mia-cover-video-panel-white-gradient-mobile, theme.$mia-color-themeMain;

		.container {
			flex-direction: column;
		}

		.mia-cover-image {
			right: $mia-cover-image-right-offset-mobile;
			height: 70%;
		}

		.mia-cover-text {
			font-size: theme.$font-size-h2;
			line-height: $mia-cover-text-line-height-mobile;
			width: 100%;
			text-align: center;
			padding: theme.$mia-padding-panel-xxl theme.$mia-padding-panel-xl 0;
		}

		.mia-play-button {
			margin: $mia-play-button-top-offset-mobile auto 0;
			float: none;
		}
	}
}
</style>
